/**
 * Match Score Breakdown Component
 * Displays detailed breakdown of how a job matches the user's profile
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { MatchScoreBreakdown as MatchScoreBreakdownData } from '../../../../../utils/data/calculate-job-match-score';
import { getMatchDonutScoreColor } from '../../../../../utils/match-donut/get-match-donut-score-color';
import { useDocumentDarkClass } from '../../../../../hooks/useDocumentDarkClass';

export interface MatchScoreBreakdownProps {
  matchScore: MatchScoreBreakdownData;
}

const ROW_DEFS: Array<{ key: keyof MatchScoreBreakdownData; id: string; defaultMessage: string }> = [
  { key: 'title', id: 'jobDetailPage.matchBreakdown.titleMatch', defaultMessage: 'Title' },
  { key: 'roleLevel', id: 'jobDetailPage.matchBreakdown.roleLevel', defaultMessage: 'Role Level' },
  { key: 'location', id: 'jobDetailPage.matchBreakdown.location', defaultMessage: 'Location' },
  { key: 'jobType', id: 'jobDetailPage.matchBreakdown.jobType', defaultMessage: 'Job Type' },
  { key: 'remoteness', id: 'jobDetailPage.matchBreakdown.remoteness', defaultMessage: 'Remoteness' },
  { key: 'salary', id: 'jobDetailPage.matchBreakdown.salary', defaultMessage: 'Salary' },
  { key: 'vacation', id: 'jobDetailPage.matchBreakdown.vacation', defaultMessage: 'Vacation' },
  { key: 'benefits', id: 'jobDetailPage.matchBreakdown.benefits', defaultMessage: 'Benefits' },
  { key: 'languages', id: 'jobDetailPage.matchBreakdown.languages', defaultMessage: 'Languages' },
];

export const MatchScoreBreakdown: React.FC<MatchScoreBreakdownProps> = ({ matchScore }) => {
  const isDark = useDocumentDarkClass();
  const mode = isDark ? 'dark' : 'light';

  return (
    <div className="mt-6 rounded-xl border border-jc-border bg-jc-surface/95 p-4">
      <h3 className="font-semibold text-lg text-jc-ink mb-3">
        <FormattedMessage
          id="jobDetailPage.matchBreakdown.title"
          defaultMessage="How this job matches your profile"
          description="Section heading for job match score breakdown showing how well the job matches user profile"
        />
      </h3>
      <div className="space-y-2.5">
        {ROW_DEFS.filter((row) => typeof matchScore[row.key] === 'number').map((row) => {
          const value = Math.max(0, Math.min(100, matchScore[row.key] as number));
          return (
            <div key={row.key} className="flex items-center gap-3 text-xs">
              <span className="w-24 flex-shrink-0 text-jc-ink-secondary">
                <FormattedMessage id={row.id} defaultMessage={row.defaultMessage} />
              </span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-jc-border dark:bg-jc-elevated" aria-hidden>
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${value}%`, backgroundColor: getMatchDonutScoreColor(value, mode) }}
                />
              </span>
              <span className="w-9 flex-shrink-0 text-right font-semibold tabular-nums text-jc-ink">{value}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
